import ImgixClient from '@imgix/js-core'

const imgixClient = new ImgixClient({
  domain: 'assets.tablelist.com',
  useHTTPS: true,
})

const imgixUrl1 = 'https://assets.tablelist.com'
const imgixUrl2 = 'https://cdn-images.tablelist.com'

export function removeUndefinedKeys<T extends Record<string, any>>(obj: T): T {
  return Object.keys(obj).reduce((acc, key) => {
    if (obj[key] !== undefined) {
      //@ts-ignore
      acc[key] = obj[key]
    }
    return acc
  }, {} as T)
}

export const imgixImageUrl = (
  url: string,
  imgxOptions?: {
    height?: number | string
    maxHeight?: number
    width?: number | 'auto' | string
    auto?: string
    fit?:
      | 'clamp'
      | 'crop'
      | 'clip'
      | 'facearea'
      | 'fill'
      | 'fillmax'
      | 'max'
      | 'min'
      | 'scale'
    dpr?: string
    ar?: string
    mask?: 'ellipse'
    fill?: 'blur' | 'solid'
    fm?: string
    quality?: number
  }
) => {
  imgxOptions = imgxOptions || {}

  const params: any = {}

  if (imgxOptions.fit) params.fit = imgxOptions.fit
  if (imgxOptions.height) params.h = imgxOptions.height
  if (imgxOptions.width) params.w = imgxOptions.width
  if (imgxOptions.dpr) params.dpr = imgxOptions.dpr
  if (imgxOptions.mask) params.mask = imgxOptions.mask
  if (imgxOptions.fill) params.fill = imgxOptions.fill
  if (imgxOptions.fm) params.fm = imgxOptions.fm
  params.quality = imgxOptions.quality ?? 75
  params.auto = imgxOptions.auto ?? 'format,compress'

  if (imgxOptions.maxHeight) params['max-h'] = imgxOptions.maxHeight

  if (!url.includes(imgixUrl1) && !url.includes(imgixUrl2)) {
    return url
  }

  const filePath = url.replace(imgixUrl1, '').replace(imgixUrl2, '')

  const finalUrl = imgixClient.buildURL(filePath, params)

  return finalUrl
}

export const combinePaths = (...args: string[]) => {
  let finalPath = ''
  ;[...args].forEach((path) => {
    if (path && path !== '') {
      path = path.startsWith('/') ? path.substring(1, path.length) : path
      path = path.endsWith('/') ? path.substring(0, path.length - 1) : path

      if (finalPath === '') {
        finalPath += path
      } else {
        finalPath += `/${path}`
      }
    }
  })

  return finalPath
}

export const addQueryStringParams = (
  str: string,
  params: Record<string, any>
): string => {
  // Determine if the input is a full URL or a relative path
  const baseUrl =
    str.startsWith('http://') || str.startsWith('https://')
      ? str
      : `http://example.com${str.startsWith('/') ? str : `/${str}`}`

  const url = new URL(baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl)

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      // Skip undefined or null values
      return
    }

    if (Array.isArray(value)) {
      // Join array values with a comma and append as a single parameter
      url.searchParams.append(
        key,
        value.filter((v) => v !== undefined && v !== null).join(',')
      )
    } else {
      // Append a single value
      url.searchParams.append(key, String(value))
    }
  })

  // If the input was a relative path, return only the path and query string
  return str.startsWith('http://') || str.startsWith('https://')
    ? url.toString()
    : `${url.pathname}${url.search}`
}

export const getQueryStringParam = (url: string, name: string) => {
  name = name.replace(/[[]/, '\\[').replace(/[\]]/, '\\]')

  const regexS = `[\\?&]${name}=([^&#]*)`
  const regex = new RegExp(regexS)
  const results = regex.exec(url)

  if (results === null) {
    return ''
  }

  const result = `${results[1]}`.trim()

  return !result || result === 'undefined' ? null : result
}

const slugDirtyChars =
  'àáâäæãåāăąçćčđďèéêëēėęěğǵḧîïíīįìıİłḿñńǹňôöòóœøōõőṕŕřßśšşșťțûüùúūǘůűųẃẍÿýžźż·/_,:;'
const slugCleanChars =
  'aaaaaaaaaacccddeeeeeeeegghiiiiiiiilmnnnnoooooooooprrsssssttuuuuuuuuuwxyyzzz------'
const slugRegex = new RegExp(slugDirtyChars.split('').join('|'), 'g')

export const slugify = (name: string): string => {
  //pulled from https://mhagemann.medium.com/the-ultimate-way-to-slugify-a-url-string-in-javascript-b8e4a0d849e1

  return (
    name
      .toLowerCase()
      .normalize('NFD')
      // Strip combining diacritical marks without relying on Unicode property escapes.
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '-') // Replace spaces with -
      .replace(slugRegex, (char) =>
        slugCleanChars.charAt(slugDirtyChars.indexOf(char))
      ) // Replace special characters
      .replace(/&/g, '-and-') // Replace & with 'and'
      .replace(/[^\w-]+/g, '') // Remove all non-word characters
      .replace(/--+/g, '-') // Replace multiple - with single -
      .replace(/^-+/, '') // Trim - from start of text
      .replace(/-+$/, '')
  ) // Trim - from end of text
}

export const getDomain = (url: string, subdomain?: boolean) => {
  url = url.replace(/(https?:\/\/)?/i, '')

  if (!subdomain) {
    const parts = url.split('.')

    url = parts.slice(parts.length - 2).join('.')
  }

  if (url.indexOf('/') !== -1) {
    const domain1 = url.split('/')[0]

    return domain1
  }

  const domain = url.startsWith('.') ? url : `.${url}`

  return domain
}
